<template>
  <div class="report-folder">
    <button
      type="button"
      class="report-folder-header"
      :aria-expanded="expanded ? 'true' : 'false'"
      @click="expanded = !expanded"
    >
      <FolderIcon class="report-folder-icon" />
      <span class="report-folder-name">{{ folder.name }}</span>
      <ChevronUpIcon v-if="expanded" class="report-folder-chevron-icon" />
      <ChevronDownIcon v-else class="report-folder-chevron-icon" />
    </button>

    <div v-if="expanded" class="report-folder-body">
      <div class="report-row-header">
        <span class="col-name">Report Name</span>
        <span class="col-refreshed">Last Refreshed</span>
        <span class="col-actions"></span>
      </div>
      <ReportRow
        v-for="report in sortedReports"
        :key="report.id"
        :report="report"
        @select="$emit('select', report)"
        @toggle-favorite="$emit('toggle-favorite', report)"
        @download="(r, fmt) => $emit('download', r, fmt)"
      />
      <p v-if="sortedReports.length === 0" class="report-empty">
        No reports in this folder.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { FolderIcon } from "@heroicons/vue/24/solid";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/vue/24/outline";
import ReportRow from "@/components/reports/ReportRow.vue";
import type { ReportFolder, ReportItem, ReportExportFormat } from "@/types/reports";

const props = defineProps<{ folder: ReportFolder }>();

defineEmits<{
  (e: "select", report: ReportItem): void;
  (e: "toggle-favorite", report: ReportItem): void;
  (e: "download", report: ReportItem, format: ReportExportFormat): void;
}>();

const expanded = ref<boolean>(true);

const sortedReports = computed<ReportItem[]>(() =>
  [...props.folder.reports].sort((a, b) => a.sortOrder - b.sortOrder)
);
</script>

<style scoped>
.report-folder {
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 12px;
}

.report-folder-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  background: #f5f5f5;
  border: none;
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  text-align: left;
}

.report-folder-icon {
  width: 20px;
  height: 20px;
  color: #2f8a5b;
  flex-shrink: 0;
}

.report-folder-name {
  flex: 1;
}

.report-folder-chevron-icon {
  width: 18px;
  height: 18px;
  color: #555;
  flex-shrink: 0;
}

.report-folder-body {
  padding: 0 0 4px;
}

.report-row-header {
  display: flex;
  align-items: center;
  padding: 8px 4px;
  border-bottom: 1px solid #eee;
  font-size: 12px;
  font-weight: 700;
  color: #666;
}

.col-name {
  flex: 1;
}

.col-refreshed {
  width: 148px;
  text-align: left;
  flex-shrink: 0;
}

.col-actions {
  width: 32px;
  flex-shrink: 0;
}

.report-empty {
  padding: 10px 4px;
  font-size: 14px;
  color: #888;
}
</style>
